import {AfterViewInit, Component, computed, ElementRef, OnDestroy, OnInit, signal, ViewChild} from "@angular/core";
import {NgbActiveModal} from "@ng-bootstrap/ng-bootstrap";
import {TranslateService} from "@ngx-translate/core";
import {FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors} from "@angular/forms";
import {MultiSelect} from "primeng/multiselect";
import {ScrollerOptions} from "primeng/api";
import {CellSelectionOptions, ColDef, GridApi, GridReadyEvent} from 'ag-grid-community';
import {Subscription} from 'rxjs';
import { NumericEditorComponent } from '../../common/cell-editors/numeric/numeric-editor.component';
import { ValidatorFactory } from '../../common/validator-factory';
import {NewHireParams, PlanParam} from '../../common/plan-param';
import { NumberUtils } from '../../../../common/utils/number.utils';
import { setGridNavigationRole, associateGridRowHeaders, enableGridControlActivation, enableGridHeaderControlArrowNav, keepFocusClearOfStickyGridColumn, confineGridHScrollToStickyColumn, clearGridSelectionOnBlur, focusOutOfGrid } from '../../../../common/utils/ag-grid-a11y';
import { associateMultiselectVisibleLabel, sanitizeMultiselectComboboxValue } from '../../../../common/utils/multiselect-a11y';
import { TimeOrDecimalEditorComponent } from '../../common/cell-editors/time-or-decimal/time-or-decimal-editor.component';
import {ValidationResult, WfmTextboxComponent} from "../../../../components/wfm-textbox/wfm-textbox.component";
import {PlanHelper} from "../../../../helpers/plan-helper";
import {Actions, ofType} from "@ngrx/effects";
import {Store} from "@ngrx/store";
import {AppActionTypes, DeleteLearningCurve, DeleteLearningCurveSuccess} from "../../../../store/actions";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {map} from "rxjs/operators";
import { PlanDetailValidationCellComponent } from "../../plan-detail-validation-cell/plan-detail-validation-cell.component";
import { LearningCurve, TrainingWeekData } from "../../../../models/new-hire";
import { DayOfWeek, DEFAULT_WORKING_DAYS } from "./working-days.util";
import { WeekDaysHeaderComponent } from "./week-days-header/week-days-header.component";


const AHT = "aht";


@Component({
  standalone: false,
  selector: "wfm-esp-learning-curve-dialog",
  templateUrl: "./learning-curve-dialog.component.html",
  styleUrls: ["./learning-curve-dialog.component.css"]
})
export class LearningCurveDialogComponent implements OnInit, OnDestroy, AfterViewInit {

  public isLoading = false;
  public enableCancel = true;

  public planName = '';
  public formSub: Subscription;
  public rowClassRules: any;
  public planId: any;
  public isEditMode = false;
  public existingLearningCurve: any = null;
  public hasClasses: boolean = false;
  public maxTrainingWeeks: number = 26; // Default fallback value
  // Plan start day of week (from new-hire settings); orders the working-days popover.
  public weekStartDay: DayOfWeek = 'MONDAY';

  readonly DEFAULT_WEEKS = 4;

  // Grid properties
  public gridApi: GridApi;
  private comboboxA11yObserver: MutationObserver | null = null;
  private comboboxLabelObserver: MutationObserver | null = null;
  private rowHeaderObserver: { disconnect(): void } | null = null;
  private controlActivation: { disconnect(): void } | null = null;
  private headerArrowNav: { disconnect(): void } | null = null;
  private stickyColumnScroll: { disconnect(): void } | null = null;
  private hScrollConfine: { disconnect(): void } | null = null;
  // Returns a plain cleanup function, not a { disconnect() } handle like the
  // helpers above it.
  private clearSelectionOnBlur: (() => void) | null = null;
  public columnDefs: ColDef[];
  public gridData: any[];
  public weekWorkingDays: Record<string, DayOfWeek[]> = {};
  public defaultColDef: ColDef = {
    sortable: false,
    filter: false,
    resizable: true,
    editable: true,
    cellStyle: { textAlign: 'right' },
    cellDataType: false
  };

  public contactTypesInput = signal<Array<{ oid: string; id: string; name: string; label: string }>>([]);
  public contactTypes = computed(() => {
    const formattedContactTypes = this.contactTypesInput().map(ct => ({
      value: {
        ...ct,
        disabled: true
      },
      isSelected: true,
      isReadOnly: true
    }));

    setTimeout(() => {
      this.notifyContactTypesListener();
      this.learningCurveForm.markAsPristine();
    }, 0);

    return formattedContactTypes;
  });

  public selectedContactTypes: any[] = [];
  public virtualScrollOptions: ScrollerOptions = {
    autoSize: false,
    scrollHeight: '120px'
  };

  @ViewChild('ctSelector') ctSelector: MultiSelect;
  @ViewChild('nameInput') learningCurveName: WfmTextboxComponent;

  public learningCurveForm: FormGroup;

  // AG Grid v32.2+ replacement for enableRangeSelection + enableFillHandle.
  public cellSelection: CellSelectionOptions = { handle: { mode: 'fill' } };

  public frameworkComponents = {
    numericEditor: NumericEditorComponent,
    timeOrDecimalEditor: TimeOrDecimalEditorComponent,
    planDetailValidationCell: PlanDetailValidationCellComponent,
    weekDaysHeader: WeekDaysHeaderComponent
  };

  public _dirtyCells = new Set<string>();
  public _invalidCells = new Set<string>();
  public _workingDaysInvalidCells = new Set<string>();
  public _isGridValid = true;
  public _weeksWithDataButNoDays: number[] = [];
  public locale = 'en-US';
  ahtValidator: any;
  productiveTimeValidator: any;
  public gridContext: any = {
    isGridValid: () => {
      return this._isGridValid;
    },
    isValidCell: (cellInfo: any) => {
      const id = this.getCellID(cellInfo);
      return !this._invalidCells.has(id) && !this._workingDaysInvalidCells.has(id);
    },
    // Surface the invalid indicator (error icon + ellipsised value) for ANY
    // over-limit value regardless of cell focus, matching the reference design
    // (plan-detail-validation-cell otherwise only shows it on the focused cell).
    alwaysShowInvalidIndicator: () => true,
    isWorkingDaysInvalidCell: (cellInfo: any) => {
      return this._workingDaysInvalidCells.has(this.getCellID(cellInfo));
    },
    workingDaysValidationMessage: (cellInfo: any) => {
      return this._workingDaysInvalidCells.has(this.getCellID(cellInfo))
        ? this.translate.instant('learning.curve.dialog.working.days.clear.values.msg.cell')
        : null;
    },
    getWorkingDays: (weekKey: string) => this.weekWorkingDays[weekKey] ?? [],
    setWorkingDays: (weekKey: string, days: DayOfWeek[]) => this.setWorkingDays(weekKey, days),
    applyWorkingDaysToAll: (weekKey: string) => this.applyWorkingDaysToAll(weekKey),
    isTableEnabled: () => !this.disableTable,
    getWeekStartDay: () => this.weekStartDay,
    // Inline "edit row" pencil support (shown by plan-detail-validation-cell on
    // the parameter row-header cell): every parameter row is editable while the
    // table is enabled, and activating the pencil starts editing the row's first
    // week cell.
    isRowEditable: () => !this.disableTable,
    startRowEditing: (node: any) => {
      if (this.disableTable || !node || !this.gridApi) {
        return;
      }
      this.gridApi.startEditingCell({ rowIndex: node.rowIndex, colKey: 'week1' });
    }
  };

  constructor(
    public activeModal: NgbActiveModal,
    public translate: TranslateService,
    private fb: FormBuilder,
    private store: Store<any>,
    private action$: Actions,
    private el: ElementRef
  ) {
    // Create validators once and store in class fields
    this.ahtValidator = ValidatorFactory.getValidator(
      PlanParam.AHT,
      { minValue: 0, maxValue: 99999.99 },
      this.translate,
      this.locale
    );
    this.productiveTimeValidator = ValidatorFactory.getValidator(
      NewHireParams.PRODUCTIVE_PERCENT,
      { minValue: 0, maxValue: 100 },
      this.translate,
      this.locale
    );
    // Initialize dynamic columns and data for 4 weeks by default
    this.columnDefs = this.getDynamicColumnDefs(4);
    this.gridData = this.getDynamicGridData(4);
    this.initWeekWorkingDays();

    this.defaultColDef = {
      sortable: false,
      filter: false,
      resizable: true,
      editable: () => !this.disableTable,
      cellStyle: { textAlign: 'right' },
      cellRenderer: "planDetailValidationCell",
      cellDataType: false,
      // Tab moves the roving focus from cell to cell inside the grid (AG Grid's
      // native behaviour, matching master). Tab is intentionally NOT suppressed
      // here so keyboard users step across the AHT / % Productive week cells the
      // same way they do on master.
      onCellValueChanged: (params: any) => {
        this.cellValueChanged(params);
      }
    }

    this.rowClassRules = {
      'editable-row': (params) => !this.disableTable,
      'readonly-row': (params) => this.disableTable
    }

    this.initHandlers();
  }

  cellValueChanged(cellInfo: any) {
    this._dirtyCells.add(this.getCellID(cellInfo));
    this.updateGridValid();
    this.updateWorkingDaysValidity();
  }

  initWeekWorkingDays(existing?: TrainingWeekData[]): void {
    this.weekWorkingDays = {};
    for (let i = 1; i <= 26; i++) {
      const existingDays = existing?.[i - 1]?.workingDays;
      this.weekWorkingDays[`week${i}`] =
        existingDays !== undefined ? [...existingDays] : [...DEFAULT_WORKING_DAYS];
    }
  }

  setWorkingDays(weekKey: string, days: DayOfWeek[]): void {
    this.weekWorkingDays[weekKey] = [...days];
    this.learningCurveForm.markAsDirty();
    this.updateWorkingDaysValidity();
    this.gridApi?.refreshCells({ force: true });
  }

  applyWorkingDaysToAll(sourceWeekKey: string): void {
    const source = [...(this.weekWorkingDays[sourceWeekKey] ?? [])];
    const weeks = this.learningCurveForm.get('classTrainingWeeks').value || 0;
    for (let i = 1; i <= weeks; i++) {
      this.weekWorkingDays[`week${i}`] = [...source];
    }
    this.learningCurveForm.markAsDirty();
    this.updateWorkingDaysValidity();
    this.gridApi?.refreshHeader();
    this.gridApi?.refreshCells({ force: true });
  }

  private initializeForm(): void {
    this.learningCurveForm = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(32)]],
      contactTypes: [[], [Validators.required, Validators.minLength(1)]],
      classTrainingWeeks: [
        Math.min(this.DEFAULT_WEEKS,this.maxTrainingWeeks),
        [
          Validators.required,
          this.validateTrainingWeeks.bind(this)]
      ]
    });
  }

  ngOnInit(): void {
    this.initializeForm();

    // Initialize edit mode if existingLearningCurve is provided
    if (this.existingLearningCurve) {
      this.initializeEditMode();
    }
  }

  get enableAccept() {
    return this.learningCurveForm.valid && this._isGridValid && !this.hasWorkingDaysError && this.hasChanges;
  }

  private initializeEditMode(): void {
    this.isEditMode = true;
    // Set form values from existing learning curve
    this.learningCurveForm.patchValue({
      name: this.existingLearningCurve.name,
      classTrainingWeeks: this.existingLearningCurve.trainingWeeks
    });

    // Set contact types
    if (this.existingLearningCurve.cts && this.contactTypesInput().length > 0) {
      const selectedCTs = this.contactTypesInput().filter(ct =>
        this.existingLearningCurve.cts.includes(ct.oid)
      );
      this.learningCurveForm.patchValue({
        contactTypes: selectedCTs
      });
    }

    // Disable training weeks field if learning curve has classes
    if (this.hasClasses) {
      this.learningCurveForm.get('classTrainingWeeks').disable();
    }

    // Update grid with existing training week data
    if (this.existingLearningCurve.trainingWeeksData) {
      this.updateGridWithExistingData();
    }

    // Mark form as pristine after initialization
    this.learningCurveForm.markAsPristine();

    // Clear any existing dirty cells since we're loading existing data
    this._dirtyCells.clear();
    this._invalidCells.clear();
  }

  private updateGridWithExistingData(): void {
    const weeks = this.existingLearningCurve.trainingWeeks;
    this.initWeekWorkingDays(this.existingLearningCurve.trainingWeeksData);
    this.columnDefs = this.getDynamicColumnDefs(weeks);
    this.gridData = this.getDynamicGridData(weeks);

    // Clear any existing dirty cells since we're loading existing data
    this._dirtyCells.clear();
    this._invalidCells.clear();

    // Populate grid with existing data
    if (this.existingLearningCurve.trainingWeeksData) {
      this.existingLearningCurve.trainingWeeksData.forEach((weekData: TrainingWeekData, index: number) => {
        const weekKey = `week${index + 1}`;

        // Update AHT row
        const ahtRow = this.gridData.find(row =>
          row.parameter === AHT
        );
        if (ahtRow && weekData.aht !== undefined) {
          ahtRow[weekKey] = { value: weekData.aht };
        }

        // Update productive percent row
        const productiveRow = this.gridData.find(row =>
          row.parameter === NewHireParams.PRODUCTIVE_PERCENT
        );
        if (productiveRow && weekData.percentageProductive !== undefined) {
          productiveRow[weekKey] = { value: weekData.percentageProductive };
        }
      });
    }
  }

  ngOnDestroy(): void {
    if (this.formSub) {
      this.formSub.unsubscribe();
    }
    this.comboboxA11yObserver?.disconnect();
    this.comboboxA11yObserver = null;
    this.comboboxLabelObserver?.disconnect();
    this.comboboxLabelObserver = null;
    this.rowHeaderObserver?.disconnect();
    this.rowHeaderObserver = null;
    this.controlActivation?.disconnect();
    this.controlActivation = null;
    this.headerArrowNav?.disconnect();
    this.headerArrowNav = null;
    this.stickyColumnScroll?.disconnect();
    this.stickyColumnScroll = null;
    // Mandatory: this helper installs a capture-phase listener on `document`, and
    // this component is a modal that is created and destroyed repeatedly.
    this.clearSelectionOnBlur?.();
    this.clearSelectionOnBlur = null;
    this.hScrollConfine?.disconnect();
    this.hScrollConfine = null;
    document.removeEventListener('keydown', this.trainingWeeksTabHandler, true);
  }

  setName(value: string) {
    if(this.learningCurveForm.get('name').value !== value) {
      this.learningCurveForm.get('name').setValue(value);
      this.learningCurveForm.markAsDirty();
    }
  }

  onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
    //this.gridApi.sizeColumnsToFit();
    // a11y (WCAG 4.1.2 / 1.3.1): AG Grid v31 hard-codes role="treegrid" on its
    // root, which makes JAWS treat this FLAT grid as a tree so its table-reading
    // keys announce the wrong row/column position. This grid has no expandable
    // rows, so role="grid" is correct and restores accurate row/column
    // navigation. The Parameter (row-header) column is deliberately NOT pinned:
    // pinning made AG Grid render every logical row twice (pinned-left +
    // center) with the same aria-rowindex, so JAWS double-counted rows and
    // announced the wrong row/column position for the row header. Leaving it in
    // the single center container keeps one DOM role="row" per logical row with
    // the rowheader natively in column 1, so JAWS announces the correct row and
    // column (ESP-18571 / ESP-18588). associateGridRowHeaders then ties each
    // data cell to that row header for the row-header announcement.
    setGridNavigationRole(this.el?.nativeElement, true);
    this.rowHeaderObserver?.disconnect();
    this.rowHeaderObserver = associateGridRowHeaders(this.el?.nativeElement);
    // a11y (WCAG 2.1.1 Keyboard): under the exit model the per-week "Week
    // controls" header trigger is reached with the Arrow keys, not Tab.
    // Activate it with Enter / Space when its header cell is focused. Body
    // cells are editable (Enter must start the editor), so they are excluded.
    this.controlActivation?.disconnect();
    this.controlActivation = enableGridControlActivation(this.el?.nativeElement, { bodyCells: false });
    // a11y (WCAG 2.1.1 Keyboard): Tab enters the grid onto the per-week "Week
    // controls" HEADER trigger (an interactive control inside the header cell),
    // not onto the header-cell wrapper. AG Grid's own Arrow navigation only
    // runs from the wrapper, so ArrowDown on that button was swallowed and the
    // editable AHT / % Productive body cells below could not be reached with
    // the keyboard (ESP-18552). This moves focus into row 0 of the column when
    // ArrowDown is pressed on a header control, from where native Arrow
    // navigation reaches every editable cell.
    this.headerArrowNav?.disconnect();
    this.headerArrowNav = enableGridHeaderControlArrowNav(this.el?.nativeElement, params.api);
    // a11y (WCAG 2.4.11 Focus Not Obscured / 2.1.1 Keyboard): the Parameter
    // column is frozen with CSS `position: sticky` (see the component CSS)
    // rather than AG Grid's `pinned: 'left'`, so AG Grid's scroll maths does
    // not know the left edge of the scroll area is permanently covered. Week 1
    // therefore stayed hidden underneath "Parameter" when arrowing LEFT (AG
    // Grid judged it "already visible" and scrolled nothing), and tabbing
    // towards an off-screen week header did not scroll at all because Tab does
    // not run through AG Grid's `ensureColumnVisible`. This corrects the
    // horizontal scroll from the focus event, so every route into a column —
    // Tab, Arrow keys, a mouse click — leaves the focused week clear of the
    // frozen column (ESP-21807).
    this.stickyColumnScroll?.disconnect();
    this.stickyColumnScroll = keepFocusClearOfStickyGridColumn(
      this.el?.nativeElement,
      params.api,
      { stickyColId: 'parameter' }
    );
    // a11y (WCAG 2.4.7 Focus Visible / ESP-21809): AG Grid keeps its focused-cell
    // and range state after focus leaves the grid entirely, because those are
    // painted from persistent marker classes (.ag-cell-focus,
    // .ag-cell-range-selected, .ag-cell-range-single-cell) rather than from
    // :focus. With enableRangeSelection + enableFillHandle that left the week
    // cell's drag-to-fill corner square painted at its bottom right after the
    // user clicked away - the cell highlight went (the component CSS masks it
    // once the body viewport is no longer :focus-within) but the dot stayed, and
    // a stale decoration with nothing focused reads as a second focus indicator.
    //
    // Clear the STATE instead of masking more of it: this removes the marker
    // classes and the range, which makes AG Grid destroy the fill-handle element
    // outright rather than leave a hidden one behind. The same helper is already
    // wired on every other range-selection grid in the app (plan-detail-v5,
    // plan-distribution-v2, historical-data); this dialog was the only one
    // missing it, which is why it needed the CSS masking at all.
    this.clearSelectionOnBlur?.();
    this.clearSelectionOnBlur = clearGridSelectionOnBlur(this.el?.nativeElement, params.api);
    // ESP-22062: the Parameter column is frozen with `position: sticky`, so it
    // still lives inside the scrolling centre container and AG Grid's horizontal
    // scrollbar spans the whole grid - the thumb travels underneath the frozen
    // column. This reproduces the geometry a real pinned column would give:
    // grow the scrollbar row's left spacer to the frozen column's width AND
    // shrink the scroll container by the same amount, which keeps the fake
    // scrollbar's travel exactly equal to the centre viewport's (see the
    // utility's comment for why growing the spacer alone breaks header/body
    // alignment at the right-hand end).
    this.hScrollConfine?.disconnect();
    this.hScrollConfine = confineGridHScrollToStickyColumn(
      this.el?.nativeElement,
      params.api,
      { stickyColId: 'parameter' }
    );
  }

  updateTable(): void {
    if (this.enableUpdateTable) {
      const weeks = this.learningCurveForm.get('classTrainingWeeks').value;
      // Update columnDefs and gridData dynamically
      this.columnDefs = this.getDynamicColumnDefs(weeks);
      this.gridData = this.getDynamicGridData(weeks);
      // Re-run working-days validity so stale entries for now-hidden weeks are cleared
      this.updateWorkingDaysValidity();
      // Do not resize columns to fit; allow horizontal scrolling
      // Mark the field as pristine after update
      const classTrainingWeeksControl = this.learningCurveForm.get('classTrainingWeeks');
      if (classTrainingWeeksControl) {
        classTrainingWeeksControl.markAsPristine();
      }

      this.learningCurveForm.markAsDirty();

      // Refresh row styles after marking as pristine since this changes disableTable
      if (this.gridApi) {
        this.gridApi.redrawRows();
      }

      // a11y (WCAG 2.4.3 Focus Order): updating the table redraws the grid rows
      // (destroying whatever was focused) AND disables the "Update Week" button
      // (the field is now pristine), so focus is lost to <body> and the next Tab
      // wrongly lands on the Parameter header. Move focus back to the training
      // weeks input the user just acted on so the tab order continues naturally.
      this.restoreFocusAfterUpdate();
    }
  }

  // a11y (WCAG 2.4.3 Focus Order / 2.1.1 Keyboard): deterministically move focus
  // from the Class Training Weeks input to the adjacent Update Weeks button on
  // Tab. Registering on `document` in the CAPTURE phase guarantees this runs
  // before any host- or template-level listener; it stays scoped to this dialog
  // instance by checking the focused input id and resolving the button within
  // this.el. When the input has focus and the Update Weeks button is enabled it
  // forces the input -> button step and stops the event so nothing re-routes it.
  // Shift+Tab and the disabled-button case fall through to natural flow.
  private trainingWeeksTabHandler = (event: KeyboardEvent): void => {
    if (event.key !== 'Tab' || event.shiftKey || event.altKey || event.ctrlKey || event.metaKey) return;
    const host = this.el?.nativeElement as HTMLElement | undefined;
    if (!host) return;
    // Match on the focused element (event.target may be a PrimeNG wrapper rather
    // than the inner <input>, so also fall back to the active element).
    const doc = host.ownerDocument;
    const isTrainingWeeksInput = (el: HTMLElement | null | undefined): boolean =>
      !!el && el.id === 'classTrainingWeeksInput' && host.contains(el);
    if (!isTrainingWeeksInput(event.target as HTMLElement | null) &&
        !isTrainingWeeksInput(doc?.activeElement as HTMLElement | null)) {
      return;
    }
    const btn = host.querySelector('.update-table-btn button') as HTMLButtonElement | null;
    // Only intercept when the Update Weeks button is actually focusable (enabled
    // and visible); a disabled button falls through to natural flow.
    if (!btn || btn.disabled || btn.offsetParent === null) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    btn.focus();
    // AG Grid's tab-guard / roving-focus machinery activates the moment the grid
    // joins the tab flow and can pull focus straight into the header cell on the
    // SAME interaction tick, skipping the Update Weeks button (the reported bug:
    // "focus flashes on the button then jumps to the Parameter header on Tab, but
    // Shift+Tab works"). Re-assert focus on the button once if it was stolen.
    requestAnimationFrame(() => {
      const now = doc?.activeElement as HTMLElement | null;
      if (!btn.disabled && btn.offsetParent !== null && now !== btn && !btn.contains(now)) {
        btn.focus();
      }
    });
  };

  private restoreFocusAfterUpdate(): void {
    requestAnimationFrame(() => {
      const input = this.el?.nativeElement?.querySelector('#classTrainingWeeksInput') as HTMLElement | null;
      input?.focus();
    });
  }

  submit(): void {
    if (this.enableAccept) {
      const formValue = this.learningCurveForm.getRawValue();
      const trainingWeeksData: TrainingWeekData[] = [];
      const weeks = formValue.classTrainingWeeks?formValue.classTrainingWeeks:0;

      for (let i = 1; i <= weeks; i++) {
        const weekKey = `week${i}`;
        const ahtRow = this.gridData.find(row => row.parameter === AHT);
        const percentageProductive = this.gridData.find(row => row.parameter === NewHireParams.PRODUCTIVE_PERCENT);

        trainingWeeksData.push({
          aht: ahtRow[weekKey]?.value || null,
          percentageProductive: percentageProductive[weekKey]?.value || null,
          workingDays: this.weekWorkingDays[weekKey] ?? []
        });
      }

      // Create the learning curve object using the proper interface
      const learningCurve: Partial<LearningCurve> = {
        name: formValue.name,
        trainingWeeks: weeks,
        cts: formValue.contactTypes.map(ct => ct.oid),
        trainingWeeksData: trainingWeeksData
      };

      if (this.isEditMode && this.existingLearningCurve) {
        // Add the learning curve ID for edit mode
        learningCurve.learningCurveId = this.existingLearningCurve.learningCurveId;

        this.store.dispatch({
          type: AppActionTypes.SaveLearningCurve,
          payload: { planId: this.planId, ...learningCurve }
        });
      } else {
        this.store.dispatch({
          type: AppActionTypes.SaveLearningCurve,
          payload: { planId: this.planId, ...learningCurve }
        });
      }

      this.isLoading = true;
      this.enableCancel = false;
    }
  }

  dismiss(): void {
    this.activeModal.dismiss();
  }

  validateTrainingWeeks(control: AbstractControl): ValidationErrors | null {
    const weeks = control.value;

    // Check if weeks has a value (not null, undefined, or empty string)
    if (weeks === null || weeks === undefined || weeks === '') {
      return { required: true };
    }

    // Check if weeks is negative
    if (weeks < 0) {
      return { invalidWeeks: true };
    }

    // Check if weeks exceeds the maximum allowed
    if (weeks > this.maxTrainingWeeks) {
      return { max: this.maxTrainingWeeks, actual: weeks };
    }

    return null; // Validation passes
  }

  get enableDelete() {
    return this.isEditMode && !this.isLoading;
  }

  get isFormValid() {
    // come back and add the contact types validation
    const trainingWeeksValid = this.disableTrainingWeeksField || this.learningCurveForm.get('classTrainingWeeks').valid;
    return this.hasValidLearningCurveName() && trainingWeeksValid && this._isGridValid && !this.hasWorkingDaysError;
  }

  get hasChanges() {
    // Check if form has been modified
    const formDirty = this.learningCurveForm.dirty;

    // Check if grid has been modified (has dirty cells)
    const gridDirty = this._dirtyCells.size > 0;

    return formDirty || gridDirty;
  }

  get enableUpdateTable() {
    return this.isTrainingWeeksValid() && !this.disableTrainingWeeksField;
  }

  isTrainingWeeksValid(): boolean {
    return this.learningCurveForm.get('classTrainingWeeks').valid && this.learningCurveForm.get('classTrainingWeeks').dirty;
  }

  get disableTable() {
    return this.learningCurveForm.get('classTrainingWeeks').invalid || this.learningCurveForm.get('classTrainingWeeks').dirty;
  }

  get disableTrainingWeeksField() {
    // Disable training weeks field when editing a learning curve that has associated classes
    // This prevents breaking existing class data by changing the training weeks
    return this.isEditMode && this.hasClasses;
  }

  getCellID(params: any): string {
    return params.data.parameter + '|' + params.colDef.field;
  }

  updateGridValid(): void {
    let result = true;
    this._dirtyCells.forEach((cellId: string) => {
      const [parameter, week] = cellId.split('|');
      const row = this.gridData.find((r) => r.parameter === parameter);
      if (row) {
        const value = row[week]?.value;
        const validator = parameter === 'aht' ? this.ahtValidator : this.productiveTimeValidator;
        const isValid = validator ? validator.isValid(value) : true;
        if (!isValid) {
          this._invalidCells.add(cellId);
        } else {
          this._invalidCells.delete(cellId);
        }
        result = result && isValid;
      }
    });
    this._isGridValid = result;
  }

  updateWorkingDaysValidity(): void {
    const weeks = this.learningCurveForm.get('classTrainingWeeks').value || 0;
    const ahtRow = this.gridData.find((r) => r.parameter === AHT);
    const productiveRow = this.gridData.find((r) => r.parameter === NewHireParams.PRODUCTIVE_PERCENT);
    const invalid: number[] = [];
    this._workingDaysInvalidCells.clear();

    for (let i = 1; i <= weeks; i++) {
      const weekKey = `week${i}`;
      const days = this.weekWorkingDays[weekKey] ?? [];
      if (days.length > 0) {
        continue;
      }
      const ahtValue = ahtRow?.[weekKey]?.value;
      const productiveValue = productiveRow?.[weekKey]?.value;
      // A 0 (or empty/blank) value is treated as "no value" — a 0-day week
      // holding only zeros/blanks is valid.
      const ahtHasValue = this.hasNonZeroValue(ahtValue);
      const productiveHasValue = this.hasNonZeroValue(productiveValue);
      if (ahtHasValue) {
        this._workingDaysInvalidCells.add(`${AHT}|${weekKey}`);
      }
      if (productiveHasValue) {
        this._workingDaysInvalidCells.add(`${NewHireParams.PRODUCTIVE_PERCENT}|${weekKey}`);
      }
      if (ahtHasValue || productiveHasValue) {
        invalid.push(i);
      }
    }
    this._weeksWithDataButNoDays = invalid;
  }

  private hasNonZeroValue(value: any): boolean {
    if (value === '' || value === null || value === undefined) {
      return false;
    }
    return Number(value) !== 0;
  }

  get hasWorkingDaysError(): boolean {
    return this._weeksWithDataButNoDays.length > 0;
  }

  floor(num: any, dec: any): string {
    if (num == null) {
      return '';
    }

    if (num?.toString().includes('.')) {
      let array = num.toString().split('.');
      let decPart = array.pop();
      if (!decPart) {
        decPart = '0';
      }
      array.push(decPart.substring(0, dec));
      let result = array.join('.');
      return result;
    } else {
      return num.toString();
    }
  }

  setProductiveTimeValue(params: any): boolean {
    let newValRaw = params.newValue;
    let oldValRaw = params.oldValue;
    //let newVal = NumberUtils.decimalOrTimeToTime(newValRaw);
    //let oldVal = NumberUtils.decimalOrTimeToTime(oldValRaw);
    if (newValRaw !== oldValRaw) {
      params.data[params.colDef.field] = { value: newValRaw };
      this._dirtyCells.add(this.getCellID(params));
      this.updateGridValid();
      this.updateWorkingDaysValidity();
      return true;
    }
    return false;
  }

  setNumericValue(params: any): boolean {
    const decimal = Number(params.data.metadata.decimal);
    let newValRaw = params.newValue;
    let oldValRaw = params.oldValue;
    let newVal = newValRaw === '' ? NaN : parseFloat(this.floor(newValRaw, decimal));
    let oldVal = oldValRaw === '' ? NaN : parseFloat(this.floor(oldValRaw, decimal));
    if (newVal !== oldVal) {
      params.data[params.colDef.field] = { value: isNaN(newVal) ? '' : newVal };
      this._dirtyCells.add(this.getCellID(params));
      this.updateGridValid();
      this.updateWorkingDaysValidity();
      return true;
    }
    return false;
  }

  // Helper to generate week columns dynamically
  // private generateWeekColumns(weeks: number) {
  //   // Always generate up to 26 weeks for data preallocation
  //   return Array.from({ length: 26 }, (_, i) => `week${i + 1}`);
  // }

  // Helper to generate column definitions dynamically
  private getDynamicColumnDefs(weeks: number): ColDef[] {
    // Only show columns up to the selected number of weeks
    const weekColumns = Array.from({ length: weeks }, (_, i) => `week${i + 1}`).map((week) => ({
      field: week,
      headerName: week.replace('week', 'Week '),
      width: 100,
      headerComponent: 'weekDaysHeader',
      resizable: false,
      // Right-align the numeric week values (matching the AHT / % Productive
      // convention and the rest of the plan grids) rather than centring them.
      cellStyle: { textAlign: 'right' },
      cellEditorSelector: (params) => {
        // Always use numericEditor for both parameters
        return { component: 'numericEditor' };
      },
      cellEditorParams: (params) => {
        return {
          origValue: params.data[params.colDef.field]?.value ?? '',
          metadata: params.data.metadata,
          zeroOnEmpty: false,
          locale: this.locale,
          allowNaN: true,
          context: {
            isLoading: () => {
              return this.isLoading;
            },
            // a11y (WCAG 2.1.1 / 2.4.3 Focus Order): this grid uses the ROVING
            // Tab model (see defaultColDef) — Tab steps from cell to cell across
            // the AHT / % Productive week cells, matching master. After the
            // numeric editor commits, honour that model: move to the next
            // (or previous) cell instead of leaving the grid outright. Only when
            // there is no next/previous cell — i.e. the true last/first cell of
            // the grid — does focus leave to the dialog's action buttons.
            // Previously this exited from EVERY edited cell, so tabbing out of
            // the last week jumped straight to Cancel and skipped the remaining
            // cells (e.g. the % Productive row below the last AHT week).
            exitGridFocus: (backwards: boolean) => {
              const moved = backwards
                ? this.gridApi?.tabToPreviousCell()
                : this.gridApi?.tabToNextCell();
              if (!moved) {
                focusOutOfGrid(this.el?.nativeElement, backwards);
              }
            }
          }
        };
      },
      valueGetter: (params) => {
        const val = params.data[params.colDef.field];
        return val && val.value !== undefined ? val.value : '';
      },
      valueSetter: (params) => {
        // Always use setNumericValue for both parameters
        return this.setNumericValue(params);
      },
      valueFormatter: (params) => {
        if (params?.data?.metadata) {
          let options = { decimal: params.data.metadata.decimal };
          return NumberUtils.formatNumberIfNecessary(params.value, this.locale, options.decimal);
        }
        return params.value;
      },
      editable: () => !this.disableTable,
      cellClassRules: {
        'invalid': (params) => !this.gridContext.isValidCell(params),
      }
    }));
    return [
      {
        field: 'parameter',
        headerName: 'Parameter',
        // a11y (WCAG 1.3.1 / 4.1.2): NOT pinned. Pinning this row-header column
        // made AG Grid render every logical row twice (pinned-left + center)
        // with the same aria-rowindex, so JAWS double-counted rows and
        // announced the wrong row/column for the header. lockPosition +
        // suppressMovable keep it as the first (leftmost) column without the
        // duplicate pinned DOM row, so it stays column 1 of the single semantic
        // row and JAWS announces the correct row/column position.
        lockPosition: true,
        suppressMovable: true,
        width: 200,
        cellStyle: { textAlign: 'left' },
        editable: false,
        // a11y (WCAG 1.3.1 Info and Relationships): the Parameter column holds
        // the row labels (AHT / % Productive), so mark its cells as row headers
        // and screen readers announce the correct row header for each data cell.
        cellAriaRole: 'rowheader',
        valueGetter: (params) => {
          return params.data.parameter === AHT ? this.translate.instant("plan.detail.aht.seconds") : this.translate.instant("plan.detail.productive.percent");
        }
      },
      ...weekColumns
    ];
  }

  // Helper to generate grid data dynamically, preserving values if possible
  private getDynamicGridData(weeks: number): any[] {
    // Always preallocate all 26 weeks in the data
    const weekFields = Array.from({length: 26}, (_, i) => `week${i + 1}`);
    const hasOldData = Array.isArray(this.gridData) && this.gridData.length > 0;
    const parameters = [
      {
        parameter: AHT,
        parameter_name: this.translate.instant("plan.detail.aht.seconds"),
        metadata: {
          decimal: 2,
          validator: {
            isValid: (value: any) => {
              return true;
            },
            msg: this.translate.instant("validation.err.actual_aht")
          }
        }
      },
      {
        parameter: NewHireParams.PRODUCTIVE_PERCENT,
        parameter_name: this.translate.instant("plan.detail.productive.percent"),
        metadata: {
          decimal: 2,
          validator: {
            isValid: (value: any) => {
              return true;
            },
            msg: this.translate.instant("validation.err.productive_percent")
          }
        }
      }
    ];
    return parameters.map((param, idx) => {
      const oldRow = hasOldData ? (this.gridData[idx] || {}) : {};
      const row: any = {parameter: param.parameter, metadata: param.metadata};
      weekFields.forEach((week) => {
        row[week] = hasOldData && oldRow[week] !== undefined ? oldRow[week] : {value: ''};
      });
      return row;
    });
  }

  ngAfterViewInit(): void {
    this.learningCurveName.validators.push(this.nameValidator.bind(this));
    // WCAG 4.1.2 Name, Role, Value / 2.5.3 Label in Name (screen reader +
    // Dragon): give the Contact Types combobox a descriptive accessible name
    // containing its visible label and selection text, and strip PrimeNG's
    // "[object Object]" value.
    this.comboboxA11yObserver = sanitizeMultiselectComboboxValue(this.el?.nativeElement);
    this.comboboxLabelObserver = associateMultiselectVisibleLabel(this.el?.nativeElement);
    // document + capture so it runs before the grid's modal-content-level Tab
    // handlers (which stop propagation) — see trainingWeeksTabHandler.
    document.addEventListener('keydown', this.trainingWeeksTabHandler, true);
  }

  nameValidator(name: string) {
    if (name.length < 1) {
      return new ValidationResult(this.translate.instant("field.required.msg"));
    } else if (PlanHelper.planNameRegExStartOrEndWithSpecialCharacter.test(name)) {
      return new ValidationResult(this.translate.instant("learning.curve.name.error.special.character.start.end"));
    } else if (!PlanHelper.planNameRegEx.test(name)) {
      return new ValidationResult(this.translate.instant("learning.curve.name.error.disallowed.characters"));
    } else {
      return new ValidationResult("");
    }
  }

  hasValidLearningCurveName() {
    const nameValue = this.learningCurveForm.get('name')?.value?.trim() || '';
    return !this.learningCurveName?.hasClientError && nameValue.length > 0;
  }

  notifyContactTypesListener() {
    if (this.ctSelector) {
      this.ctSelector.selectedOptions = this.ctSelector._options().map(opt => opt.value);
      this.ctSelector.updateModel(this.ctSelector.selectedOptions);
      this.ctSelector.onModelChange(this.ctSelector.value);
      this.ctSelector.onChange.emit({originalEvent: null, value: this.ctSelector.value});
    }
  }

  initHandlers() {
    this.action$.pipe(
      ofType(AppActionTypes.SaveLearningCurveSuccess),
      map((action: any) => action.payload),
      takeUntilDestroyed()
    ).subscribe(this.saveLearningCurveSuccessHandler.bind(this));

    this.action$.pipe(
      ofType(AppActionTypes.SaveLearningCurveFailed),
      map((action: any) => action.payload),
      takeUntilDestroyed()
    ).subscribe(this.saveLearningCurveFailureHandler.bind(this));

    this.action$.pipe(
      ofType(AppActionTypes.DeleteLearningCurveSuccess),
      map((action: any) => action.payload),
      takeUntilDestroyed()
    ).subscribe(this.deleteLearningCurveSuccessHandler.bind(this));

    this.action$.pipe(
      ofType(AppActionTypes.DeleteLearningCurveFailed),
      map((action: any) => action.payload),
      takeUntilDestroyed()
    ).subscribe(this.deleteLearningCurveFailedHandler.bind(this));
  }

  saveLearningCurveSuccessHandler(data: any) {
    let learningCurve: LearningCurve = {
      learningCurveId: data.learningCurveId,
      name: data.name,
      trainingWeeks: data.trainingWeeks,
      cts: JSON.parse(JSON.stringify(data.cts)),
      trainingWeeksData: JSON.parse(JSON.stringify(data.trainingWeeksData)),
      classes: data.classes || []
    };

    this.isLoading = false;
    this.enableCancel = true;

    // Close the dialog and return the data
    this.activeModal.close(learningCurve);
  }

  saveLearningCurveFailureHandler(error: any) {
    this.isLoading = false;
    this.enableCancel = true;
  }

  submitDelete(): void {
    if (this.isEditMode && this.existingLearningCurve) {
      this.enableCancel = false;
      this.isLoading = true;
      this.store.dispatch(new DeleteLearningCurve({
        planId: this.planId,
        learningCurveId: this.existingLearningCurve.learningCurveId
      }));
    } else {
      this.activeModal.close();
    }
  }

  deleteLearningCurveSuccessHandler(data: any) {
    this.isLoading = false;
    this.enableCancel = true;
    this.activeModal.close({ delete: true });
  }

  deleteLearningCurveFailedHandler(error: any) {
    this.isLoading = false;
    this.enableCancel = true;
  }
}
